import { type ColumnDef } from '@tanstack/react-table'
import type { ComplianceStudio } from '@artem-lex/common-server/complianceStudio'
import type { UserStandard } from '@/shared/hooks/api/userStandards'
import useI18n from '@/shared/hooks/usei18n'
import { Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { DataTableColumnHeader } from '@/components/data-table'
import {
  hasHazardsOrIfuData,
  hazardsCount,
  informationForUseClauseCount,
} from '../utils'
import { ExtractedInfoStatusCell } from './ExtractedInfoStatusCell'

export const STANDARDS_LIBRARY_FILTER_VALUES = {
  status: {
    current: 'current',
    withdrawn: 'withdrawn',
  },
  boolean: {
    yes: 'yes',
    no: 'no',
  },
  dataStatus: {
    ok: 'ok',
    noPdf: 'noPdf',
    notExtracted: 'notExtracted',
  },
} as const

type UseStandardsLibraryColumnsOptions = {
  onDeleteStandard?: (standard: UserStandard) => void
  complianceStudios?: ComplianceStudio[]
}

function compareNullableCount(
  countA: number | null,
  countB: number | null
): number {
  const valueA = countA === null ? -1 : countA
  const valueB = countB === null ? -1 : countB
  return valueA - valueB
}

function compareNullableDate(
  rawA: string | null | undefined,
  rawB: string | null | undefined
): number {
  const timeA =
    rawA != null && String(rawA).trim() !== '' ? new Date(rawA).getTime() : NaN
  const timeB =
    rawB != null && String(rawB).trim() !== '' ? new Date(rawB).getTime() : NaN

  if (Number.isNaN(timeA) && Number.isNaN(timeB)) {
    return 0
  }
  if (Number.isNaN(timeA)) {
    return 1
  }
  if (Number.isNaN(timeB)) {
    return -1
  }
  return timeA - timeB
}

export function useStandardsLibraryColumns(
  options: UseStandardsLibraryColumnsOptions = {}
) {
  const { t } = useTranslation(['standardsLibrary', 'common'])
  const { shortDateFormatterUtc } = useI18n()
  const navigate = useNavigate()

  const columns: ColumnDef<UserStandard>[] = [
    {
      accessorKey: 'reference',
      meta: { title: t('columnReference') },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnReference')} />
      ),
      cell: ({ row }) => (
        <span className='font-medium'>{row.getValue<string>('reference')}</span>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'title',
      meta: { title: t('columnTitle') },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnTitle')} />
      ),
      cell: ({ row }) => {
        const title = row.getValue<string>('title')
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='block max-w-[300px] cursor-default truncate underline decoration-dotted underline-offset-2'>
                {title}
              </span>
            </TooltipTrigger>
            <TooltipContent side='top' className='max-w-md'>
              {title}
            </TooltipContent>
          </Tooltip>
        )
      },
      enableHiding: false,
    },
    {
      id: 'status',
      meta: { title: t('columnStatus') },
      accessorFn: (row) =>
        row.inEffect
          ? STANDARDS_LIBRARY_FILTER_VALUES.status.current
          : STANDARDS_LIBRARY_FILTER_VALUES.status.withdrawn,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnStatus')} />
      ),
      cell: ({ row }) => (
        <Badge
          variant='outline'
          className={cn(
            row.original.inEffect
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          )}
        >
          {row.original.inEffect
            ? t('createNew.statusCurrent')
            : t('createNew.statusWithdrawn')}
        </Badge>
      ),
      filterFn: (row, id, value) => {
        const selectedValues = Array.isArray(value) ? value : []
        if (selectedValues.length === 0) {
          return true
        }
        return selectedValues.includes(String(row.getValue(id)))
      },
      enableHiding: false,
    },
    {
      id: 'hazards',
      meta: { title: t('columnHazards') },
      accessorFn: (row) => hazardsCount(row) ?? -1,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('columnHazards')} />
      ),
      cell: ({ row }) => {
        const count = hazardsCount(row.original)
        return (
          <span className='text-muted-foreground'>
            {count === null ? t('na') : count}
          </span>
        )
      },
      sortingFn: (rowA, rowB) =>
        compareNullableCount(
          hazardsCount(rowA.original),
          hazardsCount(rowB.original)
        ),
    },
    {
      id: 'informationForUse',
      meta: { title: t('columnInformationForUse') },
      accessorFn: (row) => informationForUseClauseCount(row) ?? -1,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('columnInformationForUse')}
        />
      ),
      cell: ({ row }) => {
        const count = informationForUseClauseCount(row.original)
        return (
          <span className='text-muted-foreground'>
            {count === null ? t('na') : count}
          </span>
        )
      },
      sortingFn: (rowA, rowB) =>
        compareNullableCount(
          informationForUseClauseCount(rowA.original),
          informationForUseClauseCount(rowB.original)
        ),
    },
    {
      id: 'withdrawalOjDate',
      meta: { title: t('columnWithdrawalOjDate') },
      accessorFn: (row) => row.withdrawalOjDate ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('columnWithdrawalOjDate')}
        />
      ),
      cell: ({ row }) => {
        const date = row.original.withdrawalOjDate
        if (!date || String(date).trim() === '') {
          return <span className='text-muted-foreground'>{t('na')}</span>
        }
        try {
          return shortDateFormatterUtc(new Date(date))
        } catch {
          return <span className='text-muted-foreground'>{t('na')}</span>
        }
      },
      sortingFn: (rowA, rowB) =>
        compareNullableDate(
          rowA.original.withdrawalOjDate,
          rowB.original.withdrawalOjDate
        ),
    },
    {
      id: 'dataStatus',
      meta: { title: t('columnExtractedInfo') },
      accessorFn: (row) => {
        if (hasHazardsOrIfuData(row)) {
          return STANDARDS_LIBRARY_FILTER_VALUES.dataStatus.ok
        }
        const hasPdf = Boolean(row.fileName) || Boolean(row.filePathInBucket)
        return hasPdf
          ? STANDARDS_LIBRARY_FILTER_VALUES.dataStatus.notExtracted
          : STANDARDS_LIBRARY_FILTER_VALUES.dataStatus.noPdf
      },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('columnExtractedInfo')}
        />
      ),
      cell: ({ row }) => <ExtractedInfoStatusCell standard={row.original} />,
      filterFn: (row, id, value) => {
        const selectedValues = Array.isArray(value) ? value : []
        if (selectedValues.length === 0) {
          return true
        }
        return selectedValues.includes(String(row.getValue(id)))
      },
      enableHiding: false,
    },
    {
      id: 'createdByUser',
      meta: { title: t('columnCreatedByUser') },
      accessorFn: (row) =>
        row.isImportedFromStandards === true
          ? STANDARDS_LIBRARY_FILTER_VALUES.boolean.no
          : STANDARDS_LIBRARY_FILTER_VALUES.boolean.yes,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('columnCreatedByUser')}
        />
      ),
      cell: ({ row }) =>
        row.original.isImportedFromStandards === true
          ? t('createdByUserNo')
          : t('createdByUserYes'),
      filterFn: (row, id, value) => {
        const selectedValues = Array.isArray(value) ? value : []
        if (selectedValues.length === 0) {
          return true
        }
        return selectedValues.includes(String(row.getValue(id)))
      },
    },
    {
      id: 'complianceStudios',
      meta: { title: t('columnComplianceStudios') },
      accessorFn: (row) => (row.complianceStudioIds ?? []).length,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('columnComplianceStudios')}
        />
      ),
      cell: ({ row }) => {
        const ids = row.original.complianceStudioIds ?? []
        if (ids.length === 0) {
          return <span className='text-muted-foreground'>{t('na')}</span>
        }
        const studioMap = new Map(
          (options.complianceStudios ?? []).map((studio) => [
            studio.id,
            studio.title,
          ])
        )
        const names = ids
          .map((id) => studioMap.get(id))
          .filter(Boolean) as string[]

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='cursor-default text-muted-foreground tabular-nums'>
                {ids.length}
              </span>
            </TooltipTrigger>
            <TooltipContent side='top' className='max-w-xs'>
              <ul className='space-y-0.5 text-left'>
                {names.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        )
      },
    },
    {
      id: 'complianceStudioId',
      header: () => null,
      cell: () => null,
      filterFn: (row, _id, value) => {
        const selectedValues = Array.isArray(value) ? value : []
        if (selectedValues.length === 0) {
          return true
        }
        const complianceStudioIds = row.original.complianceStudioIds ?? []
        return selectedValues.some((selectedValue) =>
          complianceStudioIds.includes(String(selectedValue))
        )
      },
      enableSorting: false,
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() =>
              navigate(`/standards-library/standards/${row.original.id}`)
            }
          >
            {t('common:actions.view')}
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='size-8 text-destructive hover:text-destructive'
            onClick={() => options.onDeleteStandard?.(row.original)}
            aria-label={t('common:actions.delete')}
          >
            <Trash2 className='size-4' />
          </Button>
        </div>
      ),
    },
  ]

  return columns
}
